/**
 * DisclosureDashboard Component
 *
 * Main disclosure tracking view that displays loans with:
 * - Stats overview (Total, ITOP Missing, Incomplete, MTD)
 * - Filter bar (search, lock status, ITOP status, hide completed)
 * - Expandable loan table with disclosure history
 * - CSV export
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DisclosureStats } from './DisclosureStats';
import { DisclosureFilters } from './DisclosureFilters';
import { DisclosureTable } from './DisclosureTable';
import { useDisclosureLoans } from './hooks/useDisclosureLoans';
import { useDisclosureFilters } from './hooks/useDisclosureFilters';
import { disclosureDashboardService } from '@/services/disclosureDashboardService';
import { createComponentLogger } from '@/utils/logger';
import type { SortColumn, SortDirection } from '@/types/disclosure-dashboard';

const log = createComponentLogger('DisclosureDashboard', 'disclosure-dashboard');

export function DisclosureDashboard() {

  // Filter state
  const { filters, setFilter, resetFilters, hasActiveFilters } = useDisclosureFilters();

  // Loans data
  const { loans, stats, loading, error, refetch } = useDisclosureLoans({ filters });

  // Selected IDs for export
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Expanded row
  const [expandedLoanId, setExpandedLoanId] = useState<string | null>(null);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('creation_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle sort
  const handleSort = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  // Sort loans
  const sortedLoans = useMemo(() => {
    return [...loans].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      // Handle nulls - always sort to end
      if (aVal == null && bVal == null) {
return 0;
}
      if (aVal == null) {
return 1;
}
      if (bVal == null) {
return -1;
}

      // Compare values
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [loans, sortColumn, sortDirection]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await refetch();
    toast.success('Disclosures refreshed');
    log.info('Disclosures refreshed');
  }, [refetch]);

  // Handle row expand/collapse
  const handleToggleExpand = useCallback((loanId: string) => {
    setExpandedLoanId(prev => prev === loanId ? null : loanId);
  }, []);

  // Handle selection toggle
  const handleToggleSelect = useCallback((loanId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(loans.map(l => l.id)));
    } else {
      setSelectedIds(new Set());
    }
  }, [loans]);

  // Handle CSV export
  const handleExport = useCallback(() => {
    const loansToExport = selectedIds.size > 0
      ? loans.filter(l => selectedIds.has(l.id))
      : loans;

    if (loansToExport.length === 0) {
      toast.error('No loans to export');
      return;
    }

    try {
      const csv = disclosureDashboardService.exportToCSV(loansToExport);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `disclosures_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${loansToExport.length} loans`);
      log.info('Exported disclosures', { count: loansToExport.length });
    } catch (err) {
      log.error('Export failed', {}, err as Error);
      toast.error('Export failed');
    }
  }, [loans, selectedIds]);

  // Clear selection when loans change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [loans]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-semibold">Disclosure Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Monitoring Intent to Proceed (ITOP) & Signature Compliance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {loans.length} active loans
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={loans.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              Export CSV
              {selectedIds.size > 0 && (
                <span className="ml-1 text-xs">({selectedIds.size})</span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="flex-shrink-0 px-4 py-3 border-b">
        <DisclosureStats stats={stats} loading={loading} />
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 px-4 py-3 border-b">
        <DisclosureFilters
          filters={filters}
          onFilterChange={setFilter}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
        />
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <DisclosureTable
          loans={sortedLoans}
          loading={loading}
          error={error}
          selectedIds={selectedIds}
          expandedLoanId={expandedLoanId}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onToggleSelect={handleToggleSelect}
          onSelectAll={handleSelectAll}
          onToggleExpand={handleToggleExpand}
          onSort={handleSort}
          onRefresh={handleRefresh}
        />
      </div>
    </div>
  );
}
